import React, { useEffect, useMemo, useState } from "react";
import {
	CircularProgress,
	Dialog,
	DialogTitle,
	LinearProgress,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";
import DeleteDialogStyle from "styles/application/DeleteDialogStyle";
import ColourConstants from "helpers/colourConstants";
import EMICheckbox from "components/Elements/EMICheckbox";
import CheckboxPartial from "assets/icons/checkbox-indeterminate.svg";

const ADD = DeleteDialogStyle();

const NONE = "none";
const SOME = "some";
const ALL = "all";

const coverage = (option, taskCount) => {
	if (option.assignedTaskCount === 0) return NONE;
	return option.assignedTaskCount === taskCount ? ALL : SOME;
};

const useStyles = makeStyles()(() => ({
	intro: { fontFamily: "Roboto Condensed", marginBottom: 8 },
	name: { fontFamily: "Roboto Condensed", fontSize: 15 },
	head: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: 14,
		backgroundColor: ColourConstants.tableBackground,
	},
	selectedCol: { width: 90, textAlign: "center" },
	partial: { fontFamily: "Roboto Condensed", fontSize: 13, color: ColourConstants.commonText },
	empty: { fontFamily: "Roboto Condensed", padding: 12 },
}));

/**
 * One checkbox per option, pre-filled from how many of the selected tasks
 * already have it: ticked on all, part-ticked on some, clear on none.
 *
 * Only options the user actually changes are sent, as add or remove. A
 * part-ticked row left alone stays mixed, so tasks that differ are not
 * flattened just because the dialog was opened.
 */
const BulkAssignDialog = ({
	relation,
	summary,
	isLoading,
	isSaving,
	taskCaptionPlural,
	customCaptions,
	onClose,
	onApply,
}) => {
	const { classes } = useStyles();
	const { t } = useTranslation();
	const [desired, setDesired] = useState({});

	useEffect(() => setDesired({}), [relation, summary]);

	const label = relation ? relation.caption(customCaptions) : "";
	const { taskCount, options } = summary;

	const stateOf = (option) => {
		const initial = coverage(option, taskCount);
		if (desired[option.id] === undefined) return initial;
		return desired[option.id] ? ALL : NONE;
	};

	const toggle = (option) => {
		const next = stateOf(option) !== ALL;
		setDesired((current) => ({ ...current, [option.id]: next }));
	};

	const { addIds, removeIds } = useMemo(() => {
		const add = [];
		const remove = [];
		options.forEach((option) => {
			if (desired[option.id] === undefined) return;
			const initial = coverage(option, taskCount);
			if (desired[option.id] && initial !== ALL) add.push(option.id);
			if (!desired[option.id] && initial !== NONE) remove.push(option.id);
		});
		return { addIds: add, removeIds: remove };
	}, [desired, options, taskCount]);

	const nothingChanged = addIds.length === 0 && removeIds.length === 0;

	return (
		<Dialog
			open={Boolean(relation)}
			onClose={isSaving ? undefined : onClose}
			fullWidth
		>
			{isSaving ? <LinearProgress /> : null}

			<ADD.ActionContainer>
				<DialogTitle>
					<ADD.HeaderText>
						{t("label.entityForCountEntity", { label, count: taskCount, entity: taskCaptionPlural })}
					</ADD.HeaderText>
				</DialogTitle>
				<ADD.ButtonContainer>
					<ADD.CancelButton onClick={onClose} disabled={isSaving} variant="contained">
						{t("action.cancel")}
					</ADD.CancelButton>
					<ADD.DeleteButton
						onClick={() => onApply({ addIds, removeIds })}
						disabled={isSaving || nothingChanged}
						variant="contained"
					>
						{t("action.apply")}
					</ADD.DeleteButton>
				</ADD.ButtonContainer>
			</ADD.ActionContainer>

			<ADD.DialogContent>
				{isLoading ? (
					<CircularProgress size={24} />
				) : options.length === 0 ? (
					<div className={classes.empty}>{t("message.noEntityInThisModel", { entity: label })}</div>
				) : (
					<>
						<div className={classes.intro}>
							{t("message.tickToAddToAllEntity", { count: taskCount, entity: taskCaptionPlural })}
						</div>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell className={`${classes.head} ${classes.selectedCol}`}>
										{t("label.selected")}
									</TableCell>
									<TableCell className={classes.head}>{label}</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{options.map((option) => {
									const state = stateOf(option);
									return (
										<TableRow key={option.id}>
											<TableCell className={classes.selectedCol}>
												<EMICheckbox
													state={state === ALL}
													indeterminate={state === SOME}
													indeterminateIcon={
														<img
															src={CheckboxPartial}
															alt="on some tasks"
														/>
													}
													changeHandler={() => toggle(option)}
													inputProps={{ "aria-label": option.name }}
												/>
											</TableCell>
											<TableCell className={classes.name}>
												{option.name}
												{state === SOME && (
													<span className={classes.partial}>
														{t("label.onCountOfTotal", { count: option.assignedTaskCount, total: taskCount })}
													</span>
												)}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</>
				)}
			</ADD.DialogContent>
		</Dialog>
	);
};

export default BulkAssignDialog;
